import {
  ArrowDown,
  ArrowUp,
  Crosshair,
  HelpCircle,
  MousePointer2,
  Move3D,
  Pause,
  Play,
  RotateCcw,
  SkipBack,
  SkipForward,
  Volume2,
  X,
  ZoomIn,
} from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { useEffect, useState } from "react";
import { portfolioItems, profileLinks } from "../data/content";
import type { SessionState } from "../services/api";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";
import { CarbonOrmCover } from "./CarbonOrmCover";
import { TechTerrain } from "./TechTerrain";
import { TetrisPanel } from "./TetrisPanel";
import {
  AUDIO_CONTROL_EVENT,
  AUDIO_STATE_EVENT,
  defaultAudioPlayerState,
  type AudioControlDetail,
  type AudioPlayerState,
} from "../data/audioPlaylist";
import {
  ORB_DEFENSE_CONTROL_EVENT,
  ORB_DEFENSE_PLACEABLE_TURRET_INDEXES,
  ORB_DEFENSE_STARTING_ENERGY,
  ORB_DEFENSE_STATE_EVENT,
  ORB_DEFENSE_TURRETS,
  type OrbDefenseControlDetail,
  type OrbDefenseHudState,
  type OrbDefenseProjection,
} from "../data/orbDefenseGameplay";

type PublicSiteProps = {
  page?: "blog" | "home";
  session: SessionState | null;
};

type ActiveExperience = "none" | "orb" | "galaga";
type EasterEggId = "tetris" | "galaga";

type EasterEggToastState = {
  egg: EasterEggId;
  count: number;
  nonce: number;
};

const EASTER_EGG_TOTAL = 2;

const blogParagraphs = [
  "Assessorly is where a lot of my attention goes: building property-data software that has to be useful, explainable, and steady under real operational pressure. It pulls together assessment records, valuation work, mapping, reporting, and all the small details that make a product trustworthy when people are using it for serious decisions.",
  "Harvester is the other side of that work. I like having infrastructure I can touch, inspect, break, and repair, because it keeps the abstract parts of software honest. Kubernetes, storage, networking, monitoring, and the occasional hardware surprise are all part of the same system when the goal is to keep useful services running.",
  "My servers are part lab and part discipline. The rack is where experiments become habits: clean power, boring backups, clear alerts, careful rollouts, and enough spare capacity to try things without pretending the cloud is magic. It is not glamorous, but it is mine, and it makes the work concrete.",
  "The rest of life is woven through that same pattern. I live in Colorado, build products, keep old machines useful, take care of the systems around me, and try to leave things a little more understandable than I found them. Miles Systems is the public edge of that ongoing practice.",
] as const;

export function PublicSite({ page = "home", session: _session }: PublicSiteProps) {
  const reducedMotion = usePrefersReducedMotion();
  const [activeExperience, setActiveExperience] = useState<ActiveExperience>("none");
  const [gameHud, setGameHud] = useState<OrbDefenseHudState | null>(null);
  const [gameTutorialOpen, setGameTutorialOpen] = useState(false);
  const [tetrisVisible, setTetrisVisible] = useState(false);
  const [foundEggs, setFoundEggs] = useState<EasterEggId[]>([]);
  const [eggToast, setEggToast] = useState<EasterEggToastState | null>(null);
  const currentYear = new Date().getFullYear();
  const isBlogPage = page === "blog";
  const gameActive = activeExperience === "orb";
  const galagaActive = activeExperience === "galaga";
  const experienceActive = activeExperience !== "none";
  const discoverEgg = (egg: EasterEggId) => {
    setFoundEggs((current) => {
      const next = current.includes(egg) ? current : [...current, egg];
      setEggToast({ egg, count: next.length, nonce: Date.now() });
      return next;
    });
  };
  const launchGame = () => {
    setGameTutorialOpen(true);
    setActiveExperience("orb");
  };
  const launchGalaga = () => {
    discoverEgg("galaga");
    setGameHud(null);
    setActiveExperience("galaga");
  };
  const toggleTetris = () => {
    discoverEgg("tetris");
    setTetrisVisible((visible) => !visible);
  };
  const activateBackgroundAudio = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    if (target.closest("a, button, .legacy-card, .legacy-tetris, .legacy-blog, .legacy-footer")) {
      return;
    }
    window.dispatchEvent(new Event("miles-systems:toggle-background-audio"));
  };

  useEffect(() => {
    const handleHudState = (event: Event) => {
      setGameHud((event as CustomEvent<OrbDefenseHudState>).detail);
    };

    window.addEventListener(ORB_DEFENSE_STATE_EVENT, handleHudState);
    return () => window.removeEventListener(ORB_DEFENSE_STATE_EVENT, handleHudState);
  }, []);

  useEffect(() => {
    if (activeExperience !== "orb") {
      setGameHud(null);
    }
  }, [activeExperience]);

  useEffect(() => {
    if (!eggToast) {
      return undefined;
    }
    const timeout = window.setTimeout(() => setEggToast(null), 3200);
    return () => window.clearTimeout(timeout);
  }, [eggToast]);

  return (
    <div
      className="legacy-shell"
      data-game={experienceActive ? "active" : "splash"}
      data-experience={activeExperience}
    >
      <TechTerrain reducedMotion={reducedMotion} gameActive={gameActive} galagaActive={galagaActive} />
      <header className="legacy-header" aria-hidden={experienceActive ? "true" : undefined}>
        <div className="legacy-brand" aria-label="Miles Systems">
          <a className="legacy-brand-home" href="/" aria-label="Miles Systems home">
            <img src="/TruncatedIcosahedron.svg" alt="" />
            <span aria-hidden="true">
              <strong>Miles Sys</strong>
            </span>
          </a>
          <button
            type="button"
            className="legacy-tetris-trigger"
            aria-label={tetrisVisible ? "Hide Tetris easter egg" : "Reveal Tetris easter egg"}
            aria-pressed={tetrisVisible}
            title={tetrisVisible ? "Hide Tetris" : "Reveal Tetris"}
            onClick={toggleTetris}
          >
            <span aria-hidden="true">t</span>
          </button>
          <span className="legacy-brand-tail" aria-hidden="true">
            <strong>ems</strong>
          </span>
        </div>
        <nav className="legacy-contact" aria-label="Contact">
          <a href="tel:8177893294" aria-label="Call Richard Miles">
            Call
          </a>
          <a href="mailto:Richard@Miles.Systems" aria-label="Email Richard Miles">
            Email
          </a>
          <a href="/blog" aria-current={isBlogPage ? "page" : undefined}>
            Blog
          </a>
          <a href="/tools">
            Tools
          </a>
        </nav>
      </header>

      <main
        className={isBlogPage ? "legacy-main legacy-main--blog" : "legacy-main"}
        aria-hidden={experienceActive ? "true" : undefined}
        onClick={activateBackgroundAudio}
      >
        {isBlogPage ? (
          <BlogArticle />
        ) : (
          <HomeProfile reducedMotion={reducedMotion} onLaunchGame={launchGame} tetrisVisible={tetrisVisible} />
        )}

        <footer className="legacy-footer" aria-label="Copyright">
          <strong>
            Copyri
            <button
              type="button"
              className="legacy-galaga-trigger"
              aria-label="Launch Galaga easter egg"
              title="Launch Galaga"
              onClick={launchGalaga}
            >
              g
            </button>
            ht © 2014-{currentYear} <a href="/">Richard Miles</a>
          </strong>
          <MusicControls />
        </footer>
      </main>
      <GameSurfaceControls
        visible={experienceActive}
        showProjection={gameActive}
        showTutorialButton={gameActive}
        tutorialOpen={gameTutorialOpen}
        onToggleTutorial={() => setGameTutorialOpen((open) => !open)}
      />
      <GameTutorial visible={gameActive && gameTutorialOpen} onDismiss={() => setGameTutorialOpen(false)} />
      <GameHud visible={gameActive} state={gameHud} />
      <EasterEggToast toast={eggToast} foundCount={foundEggs.length} />
    </div>
  );
}

function dispatchAudioControl(detail: AudioControlDetail) {
  window.dispatchEvent(new CustomEvent<AudioControlDetail>(AUDIO_CONTROL_EVENT, { detail }));
}

function dispatchOrbDefenseControl(detail: OrbDefenseControlDetail) {
  window.dispatchEvent(new CustomEvent<OrbDefenseControlDetail>(ORB_DEFENSE_CONTROL_EVENT, { detail }));
}

function GameSurfaceControls({
  visible,
  showProjection,
  showTutorialButton,
  tutorialOpen,
  onToggleTutorial,
}: {
  visible: boolean;
  showProjection: boolean;
  showTutorialButton: boolean;
  tutorialOpen: boolean;
  onToggleTutorial: () => void;
}) {
  if (!visible) {
    return null;
  }

  return (
    <div className="legacy-game-surface-controls" role="group" aria-label="Game surface commands">
      <MusicControls compact />
      {showProjection && <ProjectionControls />}
      {showTutorialButton && (
        <button
          type="button"
          className="legacy-game-help-toggle"
          aria-label={tutorialOpen ? "Hide controls tutorial" : "Show controls tutorial"}
          aria-pressed={tutorialOpen}
          title={tutorialOpen ? "Hide controls" : "Show controls"}
          onClick={onToggleTutorial}
        >
          <HelpCircle aria-hidden="true" size={18} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}

function GameTutorial({ visible, onDismiss }: { visible: boolean; onDismiss: () => void }) {
  if (!visible) {
    return null;
  }

  return (
    <aside className="legacy-game-tutorial" aria-label="Orb Defence controls tutorial">
      <div className="legacy-game-tutorial-heading">
        <strong>Controls</strong>
        <button type="button" aria-label="Close controls tutorial" onClick={onDismiss}>
          <X aria-hidden="true" size={16} strokeWidth={2.6} />
        </button>
      </div>
      <div className="legacy-control-guide legacy-control-guide--desktop" aria-label="Mouse and keyboard controls">
        <div>
          <Move3D aria-hidden="true" size={18} strokeWidth={2.4} />
          <span>Left-drag orbits the map.</span>
        </div>
        <div>
          <MousePointer2 aria-hidden="true" size={18} strokeWidth={2.4} />
          <span>Right-drag, Shift-drag, or Alt-drag pans across the board.</span>
        </div>
        <div>
          <ArrowUp aria-hidden="true" size={18} strokeWidth={2.4} />
          <span>Arrow keys pan across the board. Hold Shift for larger moves.</span>
        </div>
        <div>
          <ZoomIn aria-hidden="true" size={18} strokeWidth={2.4} />
          <span>Wheel over a tile to zoom toward that point.</span>
        </div>
        <div>
          <Crosshair aria-hidden="true" size={18} strokeWidth={2.4} />
          <span>Click build space to place. Click a tower to upgrade.</span>
        </div>
      </div>
      <div className="legacy-control-guide legacy-control-guide--touch" aria-label="Touch controls">
        <div>
          <Move3D aria-hidden="true" size={18} strokeWidth={2.4} />
          <span>One finger orbits. Two fingers pan. Pinch zooms.</span>
        </div>
        <div>
          <Crosshair aria-hidden="true" size={18} strokeWidth={2.4} />
          <span>Tap build space to place, then tap towers to upgrade.</span>
        </div>
      </div>
      <p>Keys: arrows pan, 1-7 towers, Space wave, 0 reset, F fullscreen.</p>
    </aside>
  );
}

function EasterEggToast({ toast, foundCount }: { toast: EasterEggToastState | null; foundCount: number }) {
  if (!toast) {
    return null;
  }

  return (
    <div className="legacy-easter-toast" role="status" aria-live="polite" data-egg={toast.egg}>
      <img src="/easter-egg.svg" alt="" />
      <strong>
        {foundCount || toast.count}/{EASTER_EGG_TOTAL} easter eggs found
      </strong>
    </div>
  );
}

function ProjectionControls() {
  const controls: { label: string; projection: OrbDefenseProjection; icon: ReactNode }[] = [
    { label: "Reset view", projection: "reset", icon: <RotateCcw aria-hidden="true" size={17} strokeWidth={2.5} /> },
    { label: "Top view", projection: "top", icon: <ArrowUp aria-hidden="true" size={18} strokeWidth={2.6} /> },
    { label: "Bottom view", projection: "bottom", icon: <ArrowDown aria-hidden="true" size={18} strokeWidth={2.6} /> },
    { label: "Front view", projection: "front", icon: <Crosshair aria-hidden="true" size={17} strokeWidth={2.4} /> },
  ];

  return (
    <div className="legacy-projection-controls" role="group" aria-label="Camera projection controls">
      {controls.map((control) => (
        <button
          type="button"
          key={control.projection}
          aria-label={control.label}
          title={control.label}
          onClick={() => dispatchOrbDefenseControl({ action: "camera-projection", projection: control.projection })}
        >
          {control.icon}
        </button>
      ))}
    </div>
  );
}

function MusicControls({ compact = false }: { compact?: boolean }) {
  const [audioState, setAudioState] = useState<AudioPlayerState>(defaultAudioPlayerState);

  const updateVolume = (volume: number) => {
    setAudioState((current) => ({ ...current, volume }));
    dispatchAudioControl({ action: "volume", volume });
  };

  useEffect(() => {
    const handleAudioState = (event: Event) => {
      setAudioState((event as CustomEvent<AudioPlayerState>).detail);
    };

    window.addEventListener(AUDIO_STATE_EVENT, handleAudioState);
    return () => window.removeEventListener(AUDIO_STATE_EVENT, handleAudioState);
  }, []);

  return (
    <div
      className={compact ? "legacy-music-controls legacy-music-controls--compact" : "legacy-music-controls"}
      role="group"
      aria-label="Music controls"
    >
      {!compact && <span className="legacy-now-playing">{audioState.trackTitle}</span>}
      <div className="legacy-music-buttons">
        <button type="button" aria-label="Previous track" onClick={() => dispatchAudioControl({ action: "previous" })}>
          <SkipBack aria-hidden="true" size={18} strokeWidth={2.4} />
        </button>
        <button
          type="button"
          aria-label={audioState.isPlaying ? "Pause" : "Play"}
          onClick={() => dispatchAudioControl({ action: "toggle" })}
        >
          {audioState.isPlaying ? (
            <Pause aria-hidden="true" size={19} strokeWidth={2.5} />
          ) : (
            <Play aria-hidden="true" size={19} strokeWidth={2.5} />
          )}
        </button>
        <button type="button" aria-label="Next track" onClick={() => dispatchAudioControl({ action: "next" })}>
          <SkipForward aria-hidden="true" size={18} strokeWidth={2.4} />
        </button>
      </div>
      <label className="legacy-volume-control">
        <Volume2 aria-hidden="true" size={18} strokeWidth={2.4} />
        <span className="sr-only">Volume</span>
        <input
          aria-label="Volume"
          type="range"
          min="0"
          max="100"
          value={Math.round(audioState.volume * 100)}
          style={{ "--volume": `${Math.round(audioState.volume * 100)}%` } as CSSProperties}
          onChange={(event) => updateVolume(Number(event.currentTarget.value) / 100)}
        />
      </label>
    </div>
  );
}

function GameHud({
  visible,
  state,
}: {
  visible: boolean;
  state: OrbDefenseHudState | null;
}) {
  if (!visible) {
    return null;
  }

  const defaultOrbCount = 5;
  const hud =
    state ??
    ({
      status: "building",
      wave: 0,
      processedWave: 0,
      energy: ORB_DEFENSE_STARTING_ENERGY,
      score: 0,
      scoreBonus: 3000,
      timeRemaining: 30,
      orbsAlive: defaultOrbCount,
      totalOrbs: defaultOrbCount,
      enemies: 0,
      spawners: 0,
      turretsPlaced: 0,
      selectedTurretIndex: 0,
      selectedTurretLabel: ORB_DEFENSE_TURRETS[0].label,
      selectedTurretCost: ORB_DEFENSE_TURRETS[0].cost,
      message: "Build defenses",
      turretOptions: ORB_DEFENSE_PLACEABLE_TURRET_INDEXES.map((index) => {
        const turret = ORB_DEFENSE_TURRETS[index] ?? ORB_DEFENSE_TURRETS[0];
        return {
          index,
          label: turret.label,
          name: turret.name,
          levelLabel: turret.levelLabel,
          rank: turret.rank,
          family: turret.family,
          description: turret.description,
          power: turret.power,
          rangeLabel: turret.rangeLabel,
          sizeLabel: turret.sizeLabel,
          costLabel: turret.costLabel,
          efficiency: turret.efficiency,
          purchaseRequirement: turret.purchaseRequirement,
          member: turret.member,
          color: turret.color,
          cost: turret.cost,
          range: turret.range,
          damage: turret.damage,
          cooldown: turret.cooldown,
          affordable: ORB_DEFENSE_STARTING_ENERGY >= turret.cost,
          selected: index === 0,
        };
      }),
    } satisfies OrbDefenseHudState);
  const selectedTurret = hud.turretOptions.find((turret) => turret.index === hud.selectedTurretIndex) ?? hud.turretOptions[0];

  return (
    <aside className="legacy-game-hud" aria-label="Orb Defence HUD">
      <div className="legacy-game-hud-stats">
        <dl>
          <div>
            <dt>Wave</dt>
            <dd>{hud.wave}</dd>
          </div>
          <div>
            <dt>Time</dt>
            <dd>{Math.max(0, Math.ceil(hud.timeRemaining))}</dd>
          </div>
          <div>
            <dt>Bonus</dt>
            <dd>+{formatHudNumber(hud.scoreBonus)}</dd>
          </div>
          <div>
            <dt>Orbs</dt>
            <dd>
              {hud.orbsAlive}/{hud.totalOrbs}
            </dd>
          </div>
          <div>
            <dt>Enemies</dt>
            <dd>{hud.enemies + hud.spawners}</dd>
          </div>
        </dl>
        <div className="legacy-game-hud-status">
          <span>{hud.status}</span>
          <strong>{hud.message}</strong>
        </div>
        {selectedTurret && (
          <div className="legacy-turret-detail" aria-label="Selected turret details">
            <TurretGlyph turret={selectedTurret} />
            <div>
              <span>Name</span>
              <strong>{selectedTurret.name}</strong>
            </div>
            <div>
              <span>Level</span>
              <strong>{selectedTurret.levelLabel}</strong>
            </div>
            <div>
              <span>Description</span>
              <strong>{selectedTurret.description}</strong>
            </div>
            <div>
              <span>Power</span>
              <strong>{selectedTurret.power}</strong>
            </div>
            <div>
              <span>Range</span>
              <strong>{selectedTurret.rangeLabel}</strong>
            </div>
            <div>
              <span>Size</span>
              <strong>{selectedTurret.sizeLabel}</strong>
            </div>
            <div>
              <span>Cost Upgrade/Total</span>
              <strong>{selectedTurret.costLabel}</strong>
            </div>
            <div>
              <span>Efficiency Damage/Cost</span>
              <strong>{selectedTurret.efficiency}</strong>
            </div>
            <div>
              <span>Purchase Requirement</span>
              <strong>{selectedTurret.purchaseRequirement}</strong>
            </div>
          </div>
        )}
      </div>
      <div className="legacy-game-turrets" role="group" aria-label="Turrets">
        {hud.turretOptions.map((turret) => (
          <button
            type="button"
            key={turret.index}
            aria-pressed={turret.selected}
            data-selected={turret.selected ? "true" : "false"}
            data-affordable={turret.affordable ? "true" : "false"}
            onClick={() => dispatchOrbDefenseControl({ action: "select-turret", index: turret.index })}
            title={`${turret.name} ${turret.levelLabel} - ${turret.power}, range ${turret.rangeLabel}, ${turret.sizeLabel}, cost ${turret.costLabel}`}
          >
            <TurretGlyph turret={turret} />
            <strong>{turret.name}</strong>
            <span>{turret.levelLabel} · {formatHudNumber(turret.cost)}</span>
          </button>
        ))}
        <button
          type="button"
          className="legacy-game-wave-button"
          aria-label="Advance wave"
          onClick={() => dispatchOrbDefenseControl({ action: "advance-wave" })}
        >
          <SkipForward aria-hidden="true" size={18} strokeWidth={2.5} />
          <span>Wave</span>
        </button>
      </div>
    </aside>
  );
}

function TurretGlyph({ turret }: { turret: OrbDefenseHudState["turretOptions"][number] }) {
  return (
    <span
      className="legacy-turret-glyph"
      data-family={turret.family}
      data-level={turret.rank}
      style={{ "--turret-color": `#${turret.color.toString(16).padStart(6, "0")}` } as CSSProperties}
      aria-hidden="true"
    >
      <span />
      <span />
      <span />
    </span>
  );
}

function formatHudNumber(value: number) {
  const absolute = Math.abs(value);
  if (absolute >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}B`;
  }
  if (absolute >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (absolute >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return String(Math.round(value));
}

function HomeProfile({
  reducedMotion,
  onLaunchGame,
  tetrisVisible,
}: {
  reducedMotion: boolean;
  onLaunchGame: () => void;
  tetrisVisible: boolean;
}) {
  return (
    <div className="legacy-home-stage" data-tetris={tetrisVisible ? "visible" : "hidden"}>
      <section className="legacy-card" aria-labelledby="profile-title">
        <CarbonOrmCover reducedMotion={reducedMotion} />
        <div className="legacy-card-body">
          <div className="legacy-profile">
            <span className="legacy-portrait-frame" tabIndex={0} aria-describedby="legacy-easter-tooltip">
              <img
                className="legacy-portrait"
                src="/milessystems/view/img/defaults/richard2.png"
                alt="Richard Tyler Miles"
              />
              <span className="legacy-easter-tooltip" id="legacy-easter-tooltip" role="tooltip">
                Two easter eggs are hidden somewhere on this site.
              </span>
            </span>
            <div className="legacy-info">
              <h1 id="profile-title">Richard Tyler Miles</h1>
              <h2>Sr. Software Developer &amp; Founder</h2>
              <p>Highlands Ranch, Colorado</p>
              <address>
                <a href="mailto:Richard@Miles.Systems">Richard@Miles.Systems</a>
                <a href="tel:8177893294">(817) 789-3294</a>
              </address>
              <div className="legacy-actions" aria-label="Profile links">
                {profileLinks.map((link) => (
                  <a className={link.className} href={link.href} key={link.label}>
                    {link.label}
                  </a>
                ))}
                <button type="button" className="legacy-run-demo-button" onClick={onLaunchGame}>
                  <Play aria-hidden="true" size={20} strokeWidth={2.6} />
                  <span>Run Demo</span>
                </button>
              </div>
            </div>
            <PortfolioList />
          </div>
        </div>
      </section>
      {tetrisVisible && <TetrisPanel />}
    </div>
  );
}

function BlogArticle() {
  return (
    <article className="legacy-blog" aria-labelledby="blog-title">
      <div className="legacy-blog-copy">
        <p className="legacy-blog-kicker">Field Notes</p>
        <h2 id="blog-title">Assessorly, Harvester, and the servers in the middle</h2>
        {blogParagraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>
      <figure className="legacy-blog-photo">
        <img
          src="/milessystems/view/img/blog/IMG_2367.jpg"
          alt="Rack of servers in Richard Miles's home lab"
        />
        <figcaption>Home-lab rack, production habits.</figcaption>
      </figure>
    </article>
  );
}

function PortfolioList() {
  return (
    <aside className="legacy-portfolio" aria-label="Portfolio links">
      {portfolioItems.map((item) => (
        <a href={item.href} key={item.label}>
          <img src={item.image} alt="" />
          <span>{item.label}</span>
        </a>
      ))}
    </aside>
  );
}
